import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";

import { t } from "i18next";

import { AddAPhoto, CheckCircle, ErrorOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import {
  DROPZONE_IMAGE_BOX_STYLE,
  DROPZONE_STYLE,
  REQUIRED_MARK_STYLE,
  getDropzoneConfig
} from "@/src/components/molecules/MediaUploadCard/DropzoneUploadCard.lib";

interface DropzoneUploadCardProps {
  isRequired?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  maxCount?: number;
  onImageUpload: (files: FileList | File[]) => void;
  showRequiredCount?: boolean;
  currentCount?: number;
  requiredCount?: number;
  countLabel?: string;
  countDisplayMode?: "progress" | "total";
  highlight?: boolean;
}

interface RequiredCountMessageProps {
  currentCount: number;
  requiredCount: number;
  countLabel?: string;
  highlight?: boolean;
}

const getRequiredCountMessage = ({
  currentCount,
  requiredCount,
  countLabel
}: RequiredCountMessageProps): string => {
  if (countLabel) {
    return countLabel;
  }
  const remaining = Math.max(0, requiredCount - currentCount);
  if (remaining === 0) {
    return t("consumer.wizard.stepMedia.allRequiredPhotosAdded");
  }
  if (remaining === 1) {
    return t("consumer.wizard.stepMedia.remainingPhoto");
  }
  return (
    t("consumer.wizard.stepMedia.remainingPhotosNeeded", {
      count: remaining
    }) ?? ""
  );
};

const RequiredCountSection = ({
  currentCount,
  requiredCount,
  countLabel,
  highlight
}: RequiredCountMessageProps) => {
  const remaining = Math.max(0, requiredCount - currentCount);
  const isAllRequiredAdded = remaining === 0;
  const message = getRequiredCountMessage({
    currentCount,
    requiredCount,
    countLabel
  });
  const continuationText = isAllRequiredAdded
    ? t("consumer.wizard.stepMedia.allRequiredPhotosAddedContinuation")
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5
      }}
    >
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontWeight:
            highlight && !isAllRequiredAdded
              ? theme.typography.fontWeightSemibold
              : theme.typography.fontWeightMedium,
          color: isAllRequiredAdded
            ? theme.palette.semantic.success.main
            : theme.palette.semantic.error.main,
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          cursor: "pointer !important"
        }}
      >
        {isAllRequiredAdded ? (
          <>
            {message}
            <CheckCircle
              sx={{
                fontSize: theme.typography.h4.fontSize,
                color: theme.palette.semantic.success.main,
                mr: 0.25,
                cursor: "pointer"
              }}
            />
          </>
        ) : (
          <>
            {highlight && (
              <ErrorOutlined
                aria-hidden="true"
                focusable="false"
                sx={{
                  fontSize: theme.typography.h4.fontSize,
                  color: theme.palette.semantic.error.main,
                  cursor: "pointer"
                }}
              />
            )}
            {message}
          </>
        )}
      </Typography>
      {continuationText && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: theme.typography.fontWeightRegular,
            color: theme.palette.text.secondary,
            textAlign: "center",
            cursor: "pointer !important"
          }}
        >
          {continuationText}
        </Typography>
      )}
    </Box>
  );
};

const getAriaLabel = (
  isLoading: boolean | undefined,
  showMaxReached: boolean,
  maxCount: number | undefined
): string => {
  if (isLoading) {
    return t("notifications.uploadingImagesInProgress") ?? "";
  }
  if (showMaxReached) {
    return t("notifications.maxImagesReached", { maxCount }) ?? "";
  }
  return t("notifications.addImagesButton") ?? "";
};

const getPrimaryLabel = (
  isLoading: boolean | undefined,
  showMaxReached: boolean,
  maxCount: number | undefined
): string => {
  if (isLoading) {
    return t("notifications.uploadingImagesInProgress");
  }
  if (showMaxReached) {
    return t("notifications.maxImagesReached", { maxCount });
  }
  return t("notifications.addImagesButton");
};

const DropzoneUploadCard = ({
  isRequired,
  isLoading,
  disabled = false,
  maxCount,
  onImageUpload,
  showRequiredCount = false,
  currentCount = 0,
  requiredCount = 0,
  countLabel,
  highlight = false
}: DropzoneUploadCardProps) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onImageUpload(acceptedFiles);
      }
    },
    [onImageUpload]
  );

  const isInactive = isLoading || disabled;
  const { getRootProps, getInputProps, isDragActive } = useDropzone(
    getDropzoneConfig(onDrop, isInactive, {})
  );

  // `disabled` is true while uploading too; only show "max images" when idle at the limit.
  const showMaxReached = disabled && !isLoading;
  const ariaLabel = getAriaLabel(isLoading, showMaxReached, maxCount);
  const primaryLabel = getPrimaryLabel(isLoading, showMaxReached, maxCount);

  return (
    <Box
      {...getRootProps()}
      sx={{
        ...DROPZONE_IMAGE_BOX_STYLE,
        ...DROPZONE_STYLE,
        cursor: "pointer",
        pointerEvents: isInactive ? "none" : "auto",
        opacity: isInactive ? 0.6 : 1,
        "& *, & *::before, & *::after, & p, & span": {
          cursor: `${isInactive ? "not-allowed" : "pointer"} !important`
        },
        ...(showMaxReached && {
          cursor: "not-allowed",
          backgroundColor: theme.palette.misc.timelineBackground
        })
      }}
      className={isDragActive ? "dropzone-active" : ""}
      role="button"
      aria-disabled={isInactive}
      aria-label={ariaLabel}
    >
      {isRequired && (
        <Box component="span" sx={REQUIRED_MARK_STYLE}>
          *
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          mb: 2,
          px: 3,
          py: 1.5,
          borderRadius: "25px",
          border: "2px solid",
          borderColor: showMaxReached ? "divider" : "text.primary",
          backgroundColor: theme.palette.background.paper,
          minWidth: "200px",
          cursor: showMaxReached ? "not-allowed" : "pointer"
        }}
      >
        {!showMaxReached && !isLoading && (
          <AddAPhoto
            color="secondary"
            sx={{ fontSize: 20, cursor: "pointer" }}
          />
        )}
        <Typography
          variant="body2"
          sx={{
            fontWeight: theme.typography.fontWeightSemibold,
            textAlign: "center",
            color: showMaxReached ? "text.disabled" : "text.primary",
            cursor: showMaxReached
              ? "not-allowed !important"
              : "pointer !important"
          }}
        >
          {primaryLabel}
        </Typography>
      </Box>
      {/* Subtitle text */}
      {!disabled && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: theme.typography.fontWeightRegular,
            textAlign: "center",
            mb: showRequiredCount ? 2 : 1,
            cursor: "pointer !important"
          }}
        >
          {t("notifications.dragOrSelectImagesDescription")}
        </Typography>
      )}
      {showRequiredCount && !disabled && !isLoading && (
        <RequiredCountSection
          currentCount={currentCount}
          requiredCount={requiredCount}
          countLabel={countLabel}
          highlight={highlight}
        />
      )}
      <input {...getInputProps()} aria-hidden />
    </Box>
  );
};

export default DropzoneUploadCard;
